import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Plus, Snowflake, Zap, Wallet2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { cn } from "@/lib/utils";
import CardFace from "@/components/CardFace";
import { CardsSkeleton, CardsUnavailable } from "@/components/CardsFallback";
import EmailRequiredDialog from "@/components/EmailRequiredDialog";
import CardsScene from "@/components/cards/CardsScene";
import ServiceShowcase from "@/components/cards/ServiceShowcase";
import PhonePayPromo from "@/components/cards/PhonePayPromo";

const CardsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Откуда человек зашёл в раздел: главный экран или «Сервисы». Метку кладут
  // сами точки входа, а мы проносим её дальше — на экран карты, чтобы «назад»
  // вернуло именно туда. Без метки (прямая ссылка) уходим в «Сервисы».
  const cameFrom = location.state?.from || "/services";
  const { api, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [config, setConfig] = useState(null);
  // Отказ списка — состояние экрана, а не тост: тост уходит, а пустой экран
  // остаётся, и человек не понимает, есть у него карты или их не смогли
  // показать. reloadKey перезапускает загрузку по кнопке «Повторить».
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Готовность спрашиваем ЗДЕСЬ, до перехода на экран выпуска. Проверка только
  // там оставляла человека на заблокированной странице: окно всплывало уже
  // после перехода, и «Позже» упирало его в экран, на котором ничего нельзя
  // сделать. Отказать нужно до того, как человек куда-то ушёл.
  const [eligibility, setEligibility] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const emailLinked = eligibility ? eligibility.emailLinked : true;

  const retry = useCallback(() => {
    haptic?.("light");
    setReloadKey((n) => n + 1);
  }, [haptic]);

  // Из списка выходим ЯВНО в сервисы, а не navigate(-1). Шаг назад по истории
  // приводил обратно на карту: при одной карте экран сам перебрасывает на неё,
  // и «назад» ходило по кругу — выйти из раздела было нельзя, а каждый круг
  // стоил шести запросов.
  const handleBack = useCallback(() => {
    haptic?.("light");
    navigate(cameFrom, { replace: true });
  }, [haptic, navigate, cameFrom]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  // Выпуск асинхронный: человек уходит с экрана оплаты на список и видит плитку
  // «Выпускается». Когда карта дозреет, он должен оказаться НА НЕЙ, а не остаться
  // перед списком, гадая, случилось ли что-нибудь. Помним карты, которые были до
  // выпуска, чтобы отличить новую от уже имевшихся.
  const watchingIssue = useRef(false);
  const openedCard = useRef(false);
  const cardsBeforeIssue = useRef(null);
  const movePolls = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const load = async (initial) => {
      if (initial) setLoading(true);
      try {
        const res = await api.get("/cards/list");
        if (cancelled) return;
        const items = res?.data?.items || [];

        // Решение о переходе принимаем ДО того, как показать список. Иначе он
        // успевает отрисоваться на долю секунды — человек с готовой картой
        // видит вспышку экрана с предложением выпустить карту, и только потом
        // попадает на свою. Пока переходим, экран остаётся скелетом.
        const goingToCard =
          !searchParams.get("list") &&
          !openedCard.current &&
          !items.some((c) => c.status === "issuing") &&
          items.length === 1 &&
          items[0].cardId &&
          items[0].status !== "closed";
        if (goingToCard) {
          openedCard.current = true;
          navigate(`/cards/${items[0].cardId}`, { replace: true, state: { from: cameFrom } });
          return;
        }

        setCards(items);
        setLoadError(null);
        // Пока что-то выпускается — переспрашиваем список. Карта появится сама,
        // без действий человека: ордер параллельно добивает фоновый поллер.
        const issuing = items.some((c) => c.status === "issuing");
        // По карте может идти пополнение или вывод — тогда баланс на плитке
        // ещё поедет, и список обязан переспросить, а не замереть на старом.
        const moving = items.some((c) => c.pendingOrder);
        if (issuing) {
          if (!watchingIssue.current) {
            watchingIssue.current = true;
            cardsBeforeIssue.current = new Set(
              items.filter((c) => c.cardId).map((c) => c.cardId)
            );
          }
          timer = setTimeout(() => load(false), 5000);
        } else if (watchingIssue.current) {
          // Выпуск только что завершился: ведём человека на его новую карту,
          // а не оставляем перед списком гадать, случилось ли что-нибудь.
          watchingIssue.current = false;
          const fresh = items.find(
            (c) => c.cardId && !cardsBeforeIssue.current?.has(c.cardId)
          );
          cardsBeforeIssue.current = null;
          if (fresh) {
            openedCard.current = true;
            haptic?.("success");
            navigate(`/cards/${fresh.cardId}`, { replace: true, state: { from: cameFrom } });
          }
        }
        // Опрос ограничен: если за две минуты движение не сядет, дело не в
        // задержке, и дёргать провайдера дальше незачем.
        if (!timer && moving && movePolls.current < 24) {
          movePolls.current += 1;
          timer = setTimeout(() => load(false), 5000);
        }
        if (!moving) movePolls.current = 0;
      } catch (err) {
        if (cancelled) return;
        // Ошибку показываем только если показывать больше нечего: сорвавшийся
        // фоновый опрос не должен стирать уже нарисованный список.
        if (initial) {
          setLoadError(err?.response?.data?.detail || { message: t("cards.load_error") });
        } else {
          timer = setTimeout(() => load(false), 5000);
        }
      } finally {
        // Скелет не снимаем, если уходим на карту: иначе список успеет мигнуть
        // между снятием загрузки и сменой маршрута.
        if (!cancelled && initial && !openedCard.current) setLoading(false);
      }
    };

    load(true);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [api, t, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/cards/eligibility")
      .then((res) => {
        if (!cancelled) setEligibility(res?.data || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  // Конфиг нужен только ради лимита карт, поэтому его отказ экран не роняет:
  // список карт без кнопки выпуска полезнее, чем заглушка вместо списка.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/cards/config")
      .then((res) => {
        if (!cancelled) setConfig(res?.data || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  // Лимит карт задаёт продукт, и он строже аккаунтного: у карты с Apple Pay
  // это одна штука на держателя. Без этой проверки экран предлагал бы выпуск,
  // который провайдер отклонит уже после списания — деньги вернулись бы, но
  // человек успел бы увидеть и списание, и ошибку.
  //
  // У 2328 в лимит держателя входят ВСЕ карты, включая закрытые и
  // заблокированные (ответ поддержки 2328, 25.09.2026), поэтому считаем все.
  const liveCards = cards.length;
  const limit = config?.maxCardsPerCardholder;
  const limitReached = Boolean(limit) && liveCards >= limit;

  const handleIssue = () => {
    haptic?.("light");
    // Нет почты — объясняем на месте и никуда не уводим. «Позже» тогда
    // оставляет человека на рабочем экране, а не на заблокированном.
    if (!emailLinked) {
      setEmailDialogOpen(true);
      return;
    }
    navigate("/cards/issue");
  };

  // После привязки возвращаем сразу к выпуску: человек шёл именно за картой.
  const goLinkEmail = useCallback(() => {
    haptic?.("light");
    navigate(
      `/profile/account?link=email&return=${encodeURIComponent("/cards/issue")}`
    );
  }, [haptic, navigate]);

  const features = [
    { icon: Zap, text: t("cards.feature_issue") },
    { icon: Wallet2, text: t("cards.feature_topup") },
    { icon: Snowflake, text: t("cards.feature_freeze") },
  ];

  // Витрина показывается ТОЛЬКО тому, у кого карты ещё нет. Человеку с картой
  // реклама карты не нужна — он её уже купил.
  const steps = [
    [t("cards.promo_step_topup"), t("cards.promo_step_topup_hint")],
    [t("cards.promo_step_issue"), t("cards.promo_step_issue_hint")],
    [t("cards.promo_step_pay"), t("cards.promo_step_pay_hint")],
  ];

  return (
    <CardsScene testId="cards-page">
      <div className="lp-bar lp-topbar sticky top-0 z-30 grid grid-cols-[2rem_1fr_2rem] items-center gap-1.5 px-4 py-3">
        {showInlineBack ? (
          <button
            type="button"
            onClick={handleBack}
            className="lp-press inline-flex h-8 w-8 items-center justify-center text-foreground"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold tracking-tight text-foreground">
          {cards.length === 0 && !loading && !loadError ? t("cards.empty_title") : t("cards.title")}
        </h1>
        <span aria-hidden="true" className="h-8 w-8" />
        <span aria-hidden="true" className="lp-hair absolute inset-x-0 bottom-0 h-px" />
      </div>

      <div className="relative z-[1] flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-5 pb-[calc(5.5rem+var(--lp-bottom))] space-y-4">
        {loading ? (
          <CardsSkeleton />
        ) : loadError ? (
          <CardsUnavailable
            title={t("cards.unavailable_title")}
            hint={loadError.message || t("cards.unavailable_hint")}
            retryLabel={t("cards.retry")}
            onRetry={retry}
          />
        ) : cards.length === 0 ? (
          <>
            {/* Наклон сажает карту на сцену: без него она висит в пустоте. */}
            <CardFace
              scheme="visa"
              className="mx-auto w-full max-w-sm -rotate-[2.6deg] scale-[0.97]"
            />

            <div className="px-2 pt-3 text-center">
              <h2 className="text-[19px] font-bold leading-tight tracking-tight text-foreground">
                {t("cards.promo_title")}
              </h2>
              <p className="mx-auto mt-1.5 max-w-[19rem] text-[13px] leading-relaxed text-muted-foreground">
                {t("cards.promo_subtitle")}
              </p>
            </div>

            <ServiceShowcase
              title={t("cards.promo_services")}
              hint={t("cards.promo_services_hint")}
            />

            <PhonePayPromo
              title={t("cards.promo_phone")}
              hint={t("cards.promo_phone_hint")}
            />

            <div className="lp-glass overflow-hidden">
              {steps.map(([title, hint], index) => (
                <div
                  key={title}
                  className={cn(
                    "flex items-start gap-3 px-3.5 py-3",
                    index > 0 && "border-t border-[var(--lp-hair)]"
                  )}
                >
                  <span className="mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold tabular-nums text-primary-foreground">
                    {index + 1}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-foreground">{title}</span>
                    <span className="mt-0.5 block text-[11px] leading-relaxed text-muted-foreground">{hint}</span>
                  </span>
                </div>
              ))}
            </div>

            <div className="lp-glass overflow-hidden">
              {features.map(({ icon: Icon, text }, index) => (
                <div
                  key={text}
                  className={cn(
                    "flex items-center gap-3 px-3.5 py-3",
                    index > 0 && "border-t border-[var(--lp-hair)]"
                  )}
                >
                  <span className="lp-tile flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]">
                    <Icon className="h-4 w-4 text-link" />
                  </span>
                  <span className="text-[13px] text-foreground">{text}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {cards.map((card) => {
              // Выпускаемая карта — это ещё не карта, а ордер: cardId у неё
              // появится только в момент завершения выпуска. Открывать её
              // некуда, поэтому плитка не кликается.
              const issuing = card.status === "issuing";
              const Tile = issuing ? "div" : "button";
              return (
                <Tile
                  key={card.cardId || card.orderId}
                  {...(issuing
                    ? {}
                    : {
                        onClick: () => {
                          haptic?.("light");
                          // Метку входа проносим и отсюда: без неё «назад» с
                          // карты уходило в «Сервисы», даже если человек зашёл
                          // с главного экрана.
                          navigate(`/cards/${card.cardId}`, { state: { from: cameFrom } });
                        },
                      })}
                  className={cn("w-full space-y-3 text-left", !issuing && "lp-press")}
                  data-testid={issuing ? `card-issuing-${card.orderId}` : `card-${card.cardId}`}
                >
                  <CardFace
                    scheme={card.scheme || config?.scheme || "visa"}
                    last4={card.last4}
                    expMonth={card.expMonth}
                    expYear={card.expYear}
                    holder={card.nameOnCard}
                    status={card.status}
                    balanceMinor={issuing ? null : card.balanceMinor}
                    statusLabel={
                      card.status === "active" ? null : t(`cards.status_${card.status}`)
                    }
                  />
                  {issuing ? (
                    <div className="lp-glass px-3.5 py-3">
                      <span className="text-xs text-muted-foreground">
                        {t("cards.issuing_tile_hint")}
                      </span>
                    </div>
                  ) : !card.last4 ? (
                    <div className="lp-glass px-3.5 py-3">
                      {/* Реквизиты провайдер дозаполняет уже после того, как
                          карта стала активной: несколько секунд она живая, но
                          безымянная. Молчать об этом — значит показать
                          человеку пустую карту без объяснений. */}
                      <span className="text-xs text-muted-foreground">
                        {t("cards.details_loading")}
                      </span>
                    </div>
                  ) : null}
                </Tile>
              );
            })}
          </>
        )}
      </div>

      {/* Главное действие экрана закреплено внизу и видно всегда. В потоке оно
          уезжало под нижний край: карта нарисована в пропорциях настоящего
          пластика, под ней список возможностей и блок сервисов — на телефоне
          кнопка оказывалась за пределами первого экрана, и её приходилось
          искать прокруткой. */}
      {!loading && !loadError && (
        <div className="lp-bar relative z-[3] px-4 py-3 pb-[calc(0.75rem+var(--lp-bottom))]">
          <span aria-hidden="true" className="lp-hair absolute inset-x-0 top-0 h-px" />
          <button
            onClick={handleIssue}
            disabled={limitReached}
            className={cn(
              "lp-press inline-flex w-full items-center justify-center gap-2 rounded-[14px] px-4 py-3.5 text-sm font-semibold",
              limitReached
                ? "border border-[var(--lp-edge)] bg-[var(--lp-tile)] text-muted-foreground"
                : "bg-primary text-primary-foreground shadow-[0_8px_20px_-10px_hsl(var(--primary))]"
            )}
            data-testid="cards-issue-cta"
          >
            <Plus className="h-4 w-4" />
            {t("cards.issue_cta")}
          </button>
          {limitReached ? (
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {t("cards.limit_reached", { count: limit })}
            </p>
          ) : !emailLinked ? (
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {t("cards.email_required_badge")}
            </p>
          ) : cards.length === 0 ? (
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {t("cards.promo_cta_note")}
            </p>
          ) : null}
        </div>
      )}

      <EmailRequiredDialog
        open={emailDialogOpen}
        onGoToProfile={goLinkEmail}
        onLater={() => setEmailDialogOpen(false)}
      />
    </CardsScene>
  );
};

export default CardsPage;
